// @ts-check
// Filtro avançado (doc 06, seção 11): chip "N regras avançadas" que abre o editor do grupo raiz, com linhas "Onde",
// "E"/"OU" (operador do grupo inteiro), regras (campo, operando e valor) e subgrupos de um nível. O editor é o
// FilterTreeEditor, o mesmo das condições do público (contrato 09 de público, seção 9), sobre o rascunho da view.
import { html } from 'htm/preact';
import { useMemo, useRef } from 'preact/hooks';
import { fieldsById } from '../../data/metadata-store.js';
import { fieldCatalogForView } from '../../lib/audience-field-catalog.js';
import { Icon } from '../../ui/icon.js';
import { Dropdown } from '../../ui/dropdown.js';
import { FilterTreeEditor } from './filter-tree-editor.js';
import { createViewFilterStore } from './view-filter-store.js';
import { getViewDraft, removeAdvancedGroup } from './view-state.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 */

/**
 * Chip do filtro avançado na linha de detalhes da barra de view; abre o editor.
 * @param {{ objectMetadata: ObjectMetadata, view: View }} props
 */
export function AdvancedFilterChip({ objectMetadata, view }) {
  const anchorRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const draft = getViewDraft(view);
  const store = useMemo(() => createViewFilterStore(view), [view]);
  const root = draft.filterGroups.value.find((group) => !group.parentViewFilterGroupId);
  if (!root) return null;
  const groupIds = new Set(draft.filterGroups.value.map((group) => group.id));
  const ruleCount = draft.filters.value.filter((filter) => filter.viewFilterGroupId && groupIds.has(filter.viewFilterGroupId)).length;
  const isOpen = draft.isAdvancedFilterOpen.value;
  const fieldCatalog = fieldCatalogForView(objectMetadata, (id) => fieldsById.value.get(id));
  const visibleFieldIds = new Set(view.viewFields.filter((viewField) => viewField.isVisible).map((viewField) => viewField.fieldMetadataId));
  return html`<div class="sort-or-filter-chip" ref=${anchorRef} data-testid="advanced-filter-chip">
    <button type="button" class="sort-or-filter-chip-main" onClick=${() => (draft.isAdvancedFilterOpen.value = !isOpen)}>
      <${Icon} name="IconFilter" size=${16} />
      <span class="sort-or-filter-chip-label">${t('parity.advancedFilter.chip', { count: ruleCount })}</span>
    </button>
    <button type="button" class="sort-or-filter-chip-remove" aria-label=${t('parity.advancedFilter.removeAll')} onClick=${() => removeAdvancedGroup(view, root.id)}>
      <${Icon} name="IconX" size=${14} />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => (draft.isAdvancedFilterOpen.value = false)} width=${650} className="advanced-filter-dropdown">
      <${FilterTreeEditor} objectMetadata=${objectMetadata} store=${store} fieldCatalog=${fieldCatalog} visibleFieldIds=${visibleFieldIds} />
    <//>
  </div>`;
}
